import React, { useEffect, useState } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { format, addDays, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';

function WeekModal({ week, onClose, onSave }) {
  const [form, setForm] = useState(week || { naam: '', startDatum: '', open: true, notitie: '' });
  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 440 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ fontSize: 20 }}>{week ? 'Week bewerken' : 'Nieuwe week'}</h2>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="field"><label>Naam week <span className="req">*</span></label>
          <input className="input" value={form.naam} onChange={e => setField('naam', e.target.value)} placeholder="Bijv. Zomerkamp week 1" /></div>
        <div className="field"><label>Startdatum (maandag) <span className="req">*</span></label>
          <input className="input" type="date" value={form.startDatum} onChange={e => setField('startDatum', e.target.value)} /></div>
        <div className="field"><label>Notitie voor vrijwilligers</label>
          <textarea className="input" value={form.notitie} onChange={e => setField('notitie', e.target.value)}
            placeholder="Bijv. Locatie, bijzonderheden..." rows={2} /></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--surface-3)', borderRadius: 'var(--radius-sm)', marginBottom: 20 }}>
          <input type="checkbox" id="open" checked={form.open} onChange={e => setField('open', e.target.checked)}
            style={{ width: 18, height: 18, accentColor: 'var(--primary)', cursor: 'pointer' }} />
          <label htmlFor="open" style={{ cursor: 'pointer', fontSize: 14, fontWeight: 500, marginBottom: 0 }}>
            Week openstellen voor beschikbaarheid
          </label>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="btn btn-secondary" onClick={onClose}>Annuleren</button>
          <button className="btn btn-primary" onClick={() => {
            if (!form.naam || !form.startDatum) return alert('Naam en startdatum zijn verplicht');
            onSave(form);
          }}>Opslaan</button>
        </div>
      </div>
    </div>
  );
}

const DAYS_NL = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];

export default function AdminWeken() {
  const [weken, setWeken] = useState([]);
  const [beschikbaar, setBeschikbaar] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'new' | weekObj
  const [expandedWeek, setExpandedWeek] = useState(null);

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    const [weekSnap, beschSnap, userSnap] = await Promise.all([
      getDocs(collection(db, 'weken')),
      getDocs(collection(db, 'beschikbaarheid')),
      getDocs(collection(db, 'users')),
    ]);
    setWeken(weekSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => a.startDatum?.localeCompare(b.startDatum)));
    setBeschikbaar(beschSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setUsers(userSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  async function handleSave(form) {
    if (modal && modal.id) {
      await updateDoc(doc(db, 'weken', modal.id), form);
    } else {
      await addDoc(collection(db, 'weken'), { ...form, createdAt: new Date().toISOString() });
    }
    setModal(null);
    loadAll();
  }

  async function toggleOpen(week) {
    await updateDoc(doc(db, 'weken', week.id), { open: !week.open });
    setWeken(prev => prev.map(w => w.id === week.id ? { ...w, open: !w.open } : w));
  }

  async function deleteWeek(id) {
    if (!window.confirm('Wil je deze week verwijderen?')) return;
    await deleteDoc(doc(db, 'weken', id));
    setWeken(prev => prev.filter(w => w.id !== id));
  }

  function getBeschikbaarForWeekDay(weekId, dateStr) {
    return beschikbaar.filter(b => b.weekId === weekId && b.datum === dateStr);
  }

  function getUserName(uid) {
    const u = users.find(u => u.id === uid);
    if (!u) return uid;
    return u.voornaam ? `${u.voornaam} ${u.achternaam || ''}` : u.email;
  }

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">📅 Weken beheer</h1>
          <p className="page-subtitle">Beheer beschikbaarheidsweken en bekijk wie er beschikbaar is</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('new')}>+ Nieuwe week</button>
      </div>

      {loading ? <p style={{ color: 'var(--text-3)' }}>Laden...</p> : weken.length === 0 ? (
        <div className="card card-padded" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📅</div>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Nog geen weken aangemaakt</h3>
          <button className="btn btn-primary" onClick={() => setModal('new')}>+ Eerste week aanmaken</button>
        </div>
      ) : weken.map(week => {
        const start = parseISO(week.startDatum);
        const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
        const isExpanded = expandedWeek === week.id;
        const totalBeschikbaar = new Set(beschikbaar.filter(b => b.weekId === week.id).map(b => b.userId)).size;

        return (
          <div key={week.id} className="card" style={{ marginBottom: 16, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: isExpanded ? '1px solid var(--border)' : 'none' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600 }}>{week.naam}</h3>
                  <span className={`badge ${week.open ? 'badge-green' : 'badge-gray'}`}>
                    {week.open ? '🟢 Open' : '🔒 Gesloten'}
                  </span>
                  <span className="badge badge-gray">{totalBeschikbaar} beschikbaar</span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 3 }}>
                  {format(start, 'd MMM', { locale: nl })} – {format(addDays(start, 6), 'd MMM yyyy', { locale: nl })}
                  {week.notitie && <span> · {week.notitie}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className={`btn btn-sm ${week.open ? 'btn-secondary' : 'btn-primary'}`} onClick={() => toggleOpen(week)}>
                  {week.open ? '🔒 Sluiten' : '🟢 Openen'}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setModal(week)}>✏ Bewerken</button>
                <button className="btn btn-danger btn-sm" onClick={() => deleteWeek(week.id)}>🗑</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setExpandedWeek(isExpanded ? null : week.id)}>
                  {isExpanded ? '▲ Inklappen' : '▼ Beschikbaarheid'}
                </button>
              </div>
            </div>

            {/* Beschikbaarheid grid */}
            {isExpanded && (
              <div style={{ padding: 20 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
                  {days.map((day, i) => {
                    const dateStr = format(day, 'yyyy-MM-dd');
                    const beschList = getBeschikbaarForWeekDay(week.id, dateStr);
                    return (
                      <div key={dateStr} style={{
                        border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                        overflow: 'hidden', minHeight: 100,
                      }}>
                        <div style={{
                          padding: '6px 8px', background: beschList.length > 0 ? 'var(--success-pale)' : 'var(--surface-3)',
                          borderBottom: '1px solid var(--border)', textAlign: 'center',
                        }}>
                          <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-3)' }}>{DAYS_NL[i]}</div>
                          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 18, fontWeight: 700 }}>{format(day, 'd')}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-3)' }}>{format(day, 'MMM', { locale: nl })}</div>
                          {beschList.length > 0 && (
                            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--primary)', marginTop: 2 }}>
                              {beschList.length} pers.
                            </div>
                          )}
                        </div>
                        <div style={{ padding: '6px 8px' }}>
                          {beschList.map(b => (
                            <div key={b.id} style={{
                              fontSize: 11, padding: '2px 6px', marginBottom: 3,
                              background: 'var(--primary-pale)', borderRadius: 4,
                              color: 'var(--primary)', fontWeight: 500,
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                              {getUserName(b.userId)}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {modal && (
        <WeekModal
          week={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
